import { uploadBenefitHeroImage } from '@ben/columbia/api';
import Banner from '@ben/orion/banner';
import Button from '@ben/orion/deprecated-button';
import FileUploader from '@ben/orion/file-uploader';
import ImageCropper from '@ben/orion/image-cropper';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { convertUrlToFile } from '@ben/shared/utils/convert-url-into-file';
import { useMutation } from '@tanstack/react-query';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css, useTheme } from 'styled-components';
import useSetDirtyValue from '../hooks/use-set-dirty-value';
import DeleteConfirmationModal from '../shared/delete-confirmation-modal';
import {
  ButtonsWrapper,
  FileUploadDescriptionContainer,
  FileUploadTitleWrapper,
  FileUploadWrapper,
  VerticalDivider,
} from '../shared/file-upload.styles';

const HERO_IMAGE_ASPECT_RATIO = 3;
const HERO_IMAGE_CROPPER_WIDTH_PX = 560;
const HERO_IMAGE_CROPPER_HEIGHT_PX = Math.round(
  HERO_IMAGE_CROPPER_WIDTH_PX / HERO_IMAGE_ASPECT_RATIO
);

interface ModbenHeroImageProps {
  isCloneBenefit: boolean;
  setIsUploadInProgress: Dispatch<SetStateAction<boolean>>;
}

const ModbenHeroImage = ({
  isCloneBenefit,
  setIsUploadInProgress,
}: ModbenHeroImageProps) => {
  const { control, getFieldState } = useFormContext<IModularBenefitDetail>();
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const theme = useTheme();
  const deleteImageConfirmationModalRef = useRef<IModalRef>(null);

  const heroImage = useWatch({
    control,
    name: 'benefitHeroImage',
    exact: true,
  });

  // image is a URL string when persisted to the server, a File object when selected locally but not yet saved
  const hasUnsavedImageFile = heroImage?.image instanceof File;

  const isBenefitHeroImageDirty = getFieldState('benefitHeroImage').isDirty;
  const isPersistedToBenefit = useMemo(() => {
    return typeof heroImage?.image === 'string' && !isBenefitHeroImageDirty;
  }, [heroImage?.image, isBenefitHeroImageDirty]);

  useEffect(() => {
    setIsUploadInProgress(hasUnsavedImageFile);
  }, [hasUnsavedImageFile, setIsUploadInProgress]);

  const { mutate: uploadHeroImage } = useMutation(
    (data: unknown) =>
      uploadBenefitHeroImage(data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    {
      onSuccess: (data) => {
        setDirtyValue('benefitHeroImage', {
          ...data,
          benefit: undefined,
        });
        toast.info(
          'Hero image uploaded successfully, but please save the benefit to reflect the changes.'
        );
      },
      onError: () => {
        toast.error('Error uploading hero image, please try again.');
      },
    }
  );

  const hasClonedHeroImageRef = useRef(false);

  useEffect(() => {
    if (!isCloneBenefit || !heroImage || hasClonedHeroImageRef.current) return;
    hasClonedHeroImageRef.current = true;

    const cloneHeroImage = async () => {
      if (typeof heroImage.image !== 'string') return;
      const file = await convertUrlToFile({
        url: heroImage.image,
      });
      uploadHeroImage({
        image: file,
      });
    };

    void cloneHeroImage();
  }, [isCloneBenefit, heroImage, uploadHeroImage]);

  return (
    <>
      <FileUploadWrapper $inProgress={hasUnsavedImageFile}>
        <FileUploadTitleWrapper>
          <Typography
            variant={TypographyVariant.h2}
            color={
              hasUnsavedImageFile
                ? theme.palette.error.main
                : theme.palette.neutral[100]
            }
          >
            Hero image
          </Typography>
          <FileUploadDescriptionContainer>
            <Typography variant={TypographyVariant.bodyMd1}>
              Upload a landscape image to display as the hero banner on the
              benefit page.
            </Typography>
            <Typography variant={TypographyVariant.bodyMd1}>
              Recommended 1200px x 400px
            </Typography>
          </FileUploadDescriptionContainer>
        </FileUploadTitleWrapper>
        {heroImage?.image ? (
          <>
            {hasUnsavedImageFile ? (
              <Banner
                variant="critical"
                message="You haven't saved the hero image. Your changes will be lost if you don't save them."
                hideIcon
              />
            ) : undefined}
            <BenefitHeroImageContainer>
              <StyledImageCropper
                src={heroImage.image}
                onCropComplete={setCroppedImage}
                aspectRatio={HERO_IMAGE_ASPECT_RATIO}
              />
              <ButtonsWrapper>
                <InlineTextButton
                  label="Delete image"
                  onClick={() => {
                    deleteImageConfirmationModalRef.current?.showModal();
                  }}
                />
                {hasUnsavedImageFile ? (
                  <>
                    <VerticalDivider />
                    <Button
                      label="Save image"
                      onClick={() => {
                        uploadHeroImage({
                          image: croppedImage,
                        });
                      }}
                      width="122px"
                    />
                  </>
                ) : undefined}
              </ButtonsWrapper>
            </BenefitHeroImageContainer>
          </>
        ) : (
          <FileUploader
            uploading={false}
            onFileUpload={(file) => {
              setDirtyValue('benefitHeroImage', {
                id: 0,
                image: file[0],
                imageAltText: '',
              });
            }}
            acceptedFileExtensions={['png', 'jpg', 'jpeg']}
          />
        )}
        <Input
          name="Hero image alt text (optional)"
          variant={InputVariant.TextArea}
          maxLength={250}
          value={heroImage?.imageAltText}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setDirtyValue('benefitHeroImage.imageAltText', event.target.value);
          }}
          width="100%"
          hintText="The hero image alt text describes your image and gives context to users who may have trouble seeing it. You can only change this if you have saved the hero image to the benefit."
          disabled={!isPersistedToBenefit || hasUnsavedImageFile}
        />
      </FileUploadWrapper>
      <DeleteConfirmationModal
        ref={deleteImageConfirmationModalRef}
        title="Delete hero image"
        message="Are you sure you want to delete the hero image?"
        onConfirm={() => {
          setDirtyValue('benefitHeroImage', undefined);
        }}
      />
    </>
  );
};

export default memo(ModbenHeroImage);

const BenefitHeroImageContainer = styled(FileUploadDescriptionContainer)`
  ${() => css`
    width: ${HERO_IMAGE_CROPPER_WIDTH_PX}px;
    align-items: flex-end;
  `}
`;

const StyledImageCropper = styled(ImageCropper)`
  width: ${HERO_IMAGE_CROPPER_WIDTH_PX}px;
  height: ${HERO_IMAGE_CROPPER_HEIGHT_PX}px;

  div {
    width: ${HERO_IMAGE_CROPPER_WIDTH_PX}px;
    height: ${HERO_IMAGE_CROPPER_HEIGHT_PX}px;
  }

  img {
    width: ${HERO_IMAGE_CROPPER_WIDTH_PX}px;
    height: ${HERO_IMAGE_CROPPER_HEIGHT_PX}px;
  }
`;
